import { formatStatus } from '@/lib/utils'
import type { ApInvoicePaymentPart } from './view-models'

const AP_INVOICE_STATUS_COLORS: Record<string, string> = {
  intake: '#0f766e',
  needs_cleanup: '#d97706',
  paid: '#16a34a',
  approved_by_pc: '#059669',
  finance_review: '#7c3aed',
  payment_ready: '#059669',
  scheduled: '#0891b2',
  processing: '#0284c7',
  needs_approval: '#b91c1c',
  operations_review: '#1d4ed8',
  wait_for_adjustment: '#f97316',
  on_hold: '#7c3aed',
  disputed: '#be123c',
  duplicate: '#64748b',
  superseded: '#64748b',
  dismissed: '#64748b',
  unknown: '#94a3b8',
}

const AP_INVOICE_STATUS_LABELS: Record<string, string> = {
  intake: 'New intake',
  needs_cleanup: 'Needs cleanup',
  paid: 'Paid',
  approved_by_pc: 'Approved',
  operations_review: 'Operations review',
  finance_review: 'Finance review',
  payment_ready: 'Payment ready',
  scheduled: 'Scheduled',
  processing: 'Processing',
  needs_approval: 'Accounting review',
  wait_for_adjustment: 'Wait for Adjustment',
  on_hold: 'On hold',
  disputed: 'Disputed',
  duplicate: 'Duplicate',
  superseded: 'Superseded',
  dismissed: 'Dismiss',
  archived: 'Archived',
  unknown: 'Unknown',
}

const AP_INVOICE_PRIORITY_COLORS: Record<string, string> = {
  pay_now: '#dc2626',
  pay_this_week: '#f97316',
  high: '#ea580c',
  low: '#64748b',
  on_hold: '#7c3aed',
  wait_on_customer: '#db2777',
  not_applicable: '#94a3b8',
  deposit_required: '#0891b2',
  approved_exception: '#2563eb',
  none: '#94a3b8',
}

const AP_INVOICE_PRIORITY_LABELS: Record<string, string> = {
  pay_now: 'Pay NOW',
  pay_this_week: 'Pay this week',
  high: 'High',
  low: 'Low',
  on_hold: 'On hold',
  wait_on_customer: 'WOC',
  not_applicable: 'N/A',
  deposit_required: 'Deposit required',
  approved_exception: 'Approved exception',
  none: 'No priority',
}

export function getInvoiceStatusStyle(status: string | null | undefined): React.CSSProperties {
  const color = AP_INVOICE_STATUS_COLORS[status ?? ''] ?? '#94a3b8'
  return {
    backgroundColor: color + '20',
    color,
    borderColor: color + '40',
  }
}

export function formatInvoiceStatus(status: string | null | undefined): string {
  if (!status) return '-'
  return AP_INVOICE_STATUS_LABELS[status] ?? formatStatus(status)
}

export function getInvoicePriorityStyle(priority: string | null | undefined): React.CSSProperties {
  const color = AP_INVOICE_PRIORITY_COLORS[priority ?? ''] ?? '#94a3b8'
  return {
    backgroundColor: color + '20',
    color,
    borderColor: color + '40',
  }
}

export function formatInvoicePriority(priority: string | null | undefined): string {
  if (!priority) return 'No priority'
  return AP_INVOICE_PRIORITY_LABELS[priority] ?? formatStatus(priority)
}

/** "Deposit due", "Balance due on delivery", "Balance overdue"… for split and COD invoices. */
export function paymentPartLabel(part: ApInvoicePaymentPart, overdue: boolean): string {
  const name = { deposit: 'Deposit', balance: 'Balance', whole: '' }[part.part]
  if (overdue) return name ? `${name} overdue` : 'Overdue'
  const when = { invoice: '', ship: ' at shipping', delivery: ' on delivery', terms: '' }[part.due_on]
  return `${name ? `${name} due` : 'Due'}${when}`
}
